import {
  ETIQUETA_ETAPA_DORE,
  ETIQUETA_ETAPA_DORE_EN,
  ETIQUETA_FALTANTE_DORE,
  ETIQUETA_FALTANTE_DORE_EN,
  type ExportacionDore,
} from '@cnc/contracts';
import {et, tr, txt} from '../i18n/idioma';
import {fecha, numero, textoOGuion} from '../utilidades/formato';
import {AYUDA_DORE} from './ayuda-dore';
import {type ColumnaDeTabla, type DefinicionDeColumnas} from './columnas';

// Columnas del seguimiento de dore (decision 0057), con el formato del
// Consolidado: se eligen, se arrastran y se ordenan, y la exportacion queda
// fija a la izquierda. `orden` es el nombre del dato: ordena el servidor.
const G = {
  principal: txt('Principal', 'Main'),
  material: txt('Material', 'Material'),
  fechas: txt('Fechas', 'Dates'),
  transporte: txt('Transporte y aduana', 'Transport and customs'),
  dinero: txt('Cobros y pagos', 'Collections and payments'),
  otros: txt('Otros', 'Other'),
};

type Col = ColumnaDeTabla<ExportacionDore, string>;
const col = (
  id: string,
  titulo: Col['titulo'],
  grupo: Col['grupo'],
  valor: (f: ExportacionDore) => string,
  opciones: {porDefecto?: boolean; ancho?: number; numerica?: boolean; sinOrden?: boolean} = {},
): Col => ({
  id,
  titulo,
  grupo,
  valor,
  orden: opciones.sinOrden ? undefined : id,
  porDefecto: opciones.porDefecto ?? true,
  ancho: opciones.ancho,
  numerica: opciones.numerica,
  ayuda: AYUDA_DORE[id],
});
// Fecha calculada por el archivo (llegada = salida + 4 dias...): con "≈".
const estimada = (valor: string | null, es: boolean) => (valor && es ? `≈ ${fecha(valor)}` : fecha(valor));

export const DEFINICION_DORE: DefinicionDeColumnas<ExportacionDore, string> = {
  clave: 'cnc.dore.seguimiento',
  fija: 'codigo',
  grupos: [G.principal, G.material, G.fechas, G.transporte, G.dinero, G.otros],
  columnas: [
    col('codigo', txt('Exportación', 'Export'), G.principal, x => x.codigo, {ancho: 150}),
    col('etapa', txt('Etapa', 'Stage'), G.principal, x => et(ETIQUETA_ETAPA_DORE, ETIQUETA_ETAPA_DORE_EN)[x.etapa], {
      ancho: 150,
    }),
    col(
      'faltan',
      txt('Falta', 'Missing'),
      G.principal,
      x =>
        x.faltan.length ? x.faltan.map(f => et(ETIQUETA_FALTANTE_DORE, ETIQUETA_FALTANTE_DORE_EN)[f]).join(' · ') : '—',
      {ancho: 200, sinOrden: true},
    ),
    col(
      'estadoArchivo',
      txt('Estado en el archivo', 'Status in the file'),
      G.otros,
      x => textoOGuion(x.estadoArchivo),
      {
        porDefecto: false,
        ancho: 140,
      },
    ),
    col('intermediaria', txt('Intermediaria', 'Intermediary'), G.principal, x => textoOGuion(x.intermediaria), {
      ancho: 170,
    }),
    col('compradorFinal', txt('Comprador final', 'Final buyer'), G.principal, x => textoOGuion(x.compradorFinal), {
      ancho: 170,
    }),
    col('referencia', txt('Ref.', 'Ref.'), G.otros, x => textoOGuion(x.referencia), {porDefecto: false, ancho: 70}),
    col('pais', txt('País', 'Country'), G.principal, x => textoOGuion(x.pais), {porDefecto: false, ancho: 90}),
    col('proveedor', txt('Proveedor', 'Supplier'), G.principal, x => textoOGuion(x.proveedor), {ancho: 170}),
    col('compra', txt('Compra (NEO)', 'Purchase (NEO)'), G.otros, x => textoOGuion(x.compra), {
      porDefecto: false,
      ancho: 110,
    }),
    col(
      'ventaDelProveedor',
      txt('Venta del proveedor', 'Supplier sale'),
      G.otros,
      x => textoOGuion(x.ventaDelProveedor),
      {
        porDefecto: false,
        ancho: 130,
      },
    ),
    col(
      'facturaProveedor',
      txt('Factura del proveedor', 'Supplier invoice'),
      G.otros,
      x => textoOGuion(x.facturaProveedor),
      {
        porDefecto: false,
        ancho: 130,
      },
    ),
    col(
      'exportacionProveedor',
      txt('Exportación del proveedor', 'Supplier export'),
      G.otros,
      x => textoOGuion(x.exportacionProveedor),
      {porDefecto: false, ancho: 120},
    ),
    col('tipoDeMaterial', txt('Tipo', 'Type'), G.material, x => textoOGuion(x.tipoDeMaterial), {
      porDefecto: false,
      ancho: 90,
    }),
    col('barras', txt('Barras', 'Bars'), G.material, x => numero(x.barras), {numerica: true, ancho: 80}),
    col('barrasDePlata', txt('Barras de plata', 'Silver bars'), G.material, x => numero(x.barrasDePlata), {
      porDefecto: false,
      numerica: true,
      ancho: 100,
    }),
    col('brutoG', txt('Bruto g', 'Gross g'), G.material, x => numero(x.brutoG, 2), {
      porDefecto: false,
      numerica: true,
      ancho: 110,
    }),
    col('oroG', txt('Oro g', 'Gold g'), G.material, x => numero(x.oroG, 2), {numerica: true, ancho: 110}),
    col('oroOz', txt('Oro oz', 'Gold oz'), G.material, x => numero(x.oroOz, 3), {
      porDefecto: false,
      numerica: true,
      ancho: 100,
    }),
    col('plataG', txt('Plata g', 'Silver g'), G.material, x => numero(x.plataG, 2), {numerica: true, ancho: 110}),
    col('plataOz', txt('Plata oz', 'Silver oz'), G.material, x => numero(x.plataOz, 3), {
      porDefecto: false,
      numerica: true,
      ancho: 100,
    }),
    col('fechaDocumentos', txt('Documentos', 'Documents'), G.fechas, x => fecha(x.fechaDocumentos), {ancho: 110}),
    col('fechaExportacion', txt('Exportación', 'Export date'), G.fechas, x => fecha(x.fechaExportacion), {ancho: 110}),
    col('llegada', txt('Llegada', 'Arrival'), G.fechas, x => estimada(x.llegada, x.llegadaEstimada), {ancho: 120}),
    col(
      'llegadaRefineria',
      txt('Llegada a la refinería', 'Arrival at refinery'),
      G.fechas,
      x => estimada(x.llegadaRefineria, x.llegadaRefineriaEstimada),
      {porDefecto: false, ancho: 130},
    ),
    col('servicio', txt('Servicio', 'Service'), G.transporte, x => textoOGuion(x.servicio), {
      porDefecto: false,
      ancho: 80,
    }),
    col('transportadora', txt('Transportadora', 'Carrier'), G.transporte, x => textoOGuion(x.transportadora), {
      porDefecto: false,
      ancho: 120,
    }),
    col('mawb', 'MAWB', G.transporte, x => textoOGuion(x.mawb), {porDefecto: false, ancho: 140}),
    col('hawb', 'HAWB', G.transporte, x => textoOGuion(x.hawb), {porDefecto: false, ancho: 130}),
    col('mawbConexion', txt('MAWB de conexión', 'Connecting MAWB'), G.transporte, x => textoOGuion(x.mawbConexion), {
      porDefecto: false,
      ancho: 140,
    }),
    col('radicadoVuce', txt('Radicado VUCE', 'VUCE filing'), G.transporte, x => textoOGuion(x.radicadoVuce), {
      porDefecto: false,
      ancho: 170,
    }),
    col('aprobacionVuce', txt('Aprobación VUCE', 'VUCE approval'), G.transporte, x => fecha(x.aprobacionVuce), {
      porDefecto: false,
      ancho: 120,
    }),
    col(
      'valorProveedorUsd',
      txt('Valor proveedor USD', 'Supplier value USD'),
      G.dinero,
      x => numero(x.valorProveedorUsd, 2),
      {
        porDefecto: false,
        numerica: true,
        ancho: 140,
      },
    ),
    col('valorVentaUsd', txt('Valor venta USD', 'Sale value USD'), G.dinero, x => numero(x.valorVentaUsd, 2), {
      numerica: true,
      ancho: 140,
    }),
    col('primerPagoUsd', txt('Primer pago USD', 'First payment USD'), G.dinero, x => numero(x.primerPagoUsd, 2), {
      numerica: true,
      ancho: 140,
    }),
    col(
      'primerPagoEl',
      txt('Primer pago el', 'First payment on'),
      G.dinero,
      x => estimada(x.primerPagoEl, x.primerPagoEstimado),
      {ancho: 120},
    ),
    col('motivoDeDemora', txt('Motivo de demora', 'Delay reason'), G.dinero, x => textoOGuion(x.motivoDeDemora), {
      porDefecto: false,
      ancho: 170,
    }),
    col('saldoUsd', txt('Saldo USD', 'Balance USD'), G.dinero, x => numero(x.saldoUsd, 2), {
      porDefecto: false,
      numerica: true,
      ancho: 130,
    }),
    col(
      'pagoProveedorUsd',
      txt('Pago al proveedor USD', 'Payment to supplier USD'),
      G.dinero,
      x => numero(x.pagoProveedorUsd, 2),
      {porDefecto: false, numerica: true, ancho: 150},
    ),
    col(
      'pagoProveedorEl',
      txt('Pago al proveedor el', 'Paid to supplier on'),
      G.dinero,
      x => estimada(x.pagoProveedorEl, x.pagoProveedorEstimado),
      {porDefecto: false, ancho: 130},
    ),
    col(
      'resultadoEnsayo',
      txt('Ensayo', 'Assay'),
      G.dinero,
      x =>
        x.resultadoEnsayo === 'aprobado'
          ? tr('Aprobado', 'Approved')
          : x.resultadoEnsayo === 'reanalisis'
            ? tr('Reanálisis', 'Re-assay')
            : '—',
      {ancho: 110},
    ),
    col(
      'liquidacionCompradorUsd',
      txt('Liquidación del comprador USD', 'Buyer settlement USD'),
      G.dinero,
      x => numero(x.liquidacionCompradorUsd, 2),
      {porDefecto: false, numerica: true, ancho: 160},
    ),
    col('comentarios', txt('Comentarios', 'Comments'), G.otros, x => textoOGuion(x.comentarios), {
      porDefecto: false,
      ancho: 220,
      sinOrden: true,
    }),
  ],
};
